import useIntl from '@/utils/intl'
import React, { FunctionComponent, useEffect, useReducer, useRef } from 'react'
import { Obj } from '..'
import { useMobiApi } from '@/services/mobi-api'
import {
  Categories,
  ChoosenInfo,
  GetPackageInfo,
  PackageDetailInfo,
  SavePackageInfoReq,
  SubCategories,
  serviceDetail
} from '@/services/mobi-api/numberv2/types'
import Loader from '@/components/ui/loader'
import { Checkbox, MenuItem, Tab, Tabs, TextField } from '@mui/material'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import classNames from 'classnames'
import { CheckCircle, RadioButtonUncheckedOutlined } from '@mui/icons-material'
import { useToast } from '@/context/ToastContext'
import { useSearchParams } from 'next/navigation'
import { useAuth } from '@/context/AuthContext'
import { Locale, useLanguage } from '@/context/LanguageContext'
import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import { moneyFormatter } from '@/utils/helpers'
import { IMG_URL } from '@/utils/constants'
import { useTheme } from '@/context/ThemeContext'

interface Props {
  next: (obj: Obj) => void
  back: () => void
  instanceId: string
  referralCode?: string
  onSelectionChange?: (info: ChoosenInfo) => void
}

interface State {
  package?: GetPackageInfo
  loading?: boolean
  choosenCategory?: Categories
  choosenSubCat?: SubCategories
  choosenCallMM?: serviceDetail
  choosenDataMM?: serviceDetail
  choosenSmsMM?: serviceDetail
  choosenEntertainmentMM?: serviceDetail[]
  choosenVideoStreamingMM?: serviceDetail
  choosenPackage?: PackageDetailInfo
  lang?: string
}

const ChoosePackage: FunctionComponent<Props> = ({
  next,
  back,
  instanceId,
  referralCode,
  onSelectionChange
}) => {
  const { translate } = useIntl()
  const { showError } = useToast()
  const { numberv2 } = useMobiApi()
  const { isWebView } = useAuth()
  const { locale, setLocale } = useLanguage()
  const searchParams = useSearchParams()
  const [state, setState] = useReducer(
    (state: State, newState: State) => ({ ...state, ...newState }),
    {}
  )
  const lang = searchParams.get('lang')
  const [selectedAddonTab, setSelectedAddonTab] = React.useState(0)
  const { theme } = useTheme()

  const handleSubCategoryClick = (item: SubCategories): void => {
    setState({
      choosenSubCat: item,
      choosenPackage: undefined,
      choosenCallMM: undefined,
      choosenSmsMM: undefined,
      choosenDataMM: undefined,
      choosenVideoStreamingMM: undefined,
      choosenEntertainmentMM: undefined
    })
    if (item?.packages?.length === 1) {
      const defaultPackage = item.packages[0]
      setState({ choosenPackage: defaultPackage })
      if (onSelectionChange)
        onSelectionChange({
          choosenPackage: defaultPackage,
          choosenCallMM: undefined,
          choosenDataMM: undefined,
          choosenSmsMM: undefined,
          choosenVideoStreamingMM: undefined,
          choosenEntertainmentMM: []
        })
    } else {
      if (onSelectionChange) onSelectionChange({})
    }
  }

  const addonPackages = state.package?.categories
    .flatMap((cat) => cat.subCategories)
    .filter((sub) => sub.selectedType === 'SELECT_SERVICE')
    .flatMap((sub) => sub.packages)
    .filter((pkg) => pkg.isAdditionalService === true)

  useEffect(() => {
    if (lang !== null && lang !== '') {
      setLocale(lang as Locale)
    }
    // return () => {
    //   controller.abort()
    // }
  }, [lang])

  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const controller = new AbortController()
    void fetchPackage(locale, controller)
    return () => {
      controller.abort()
    }
  }, [locale])

  const fetchPackage = async (
    lang: string,
    controller: AbortController
  ): Promise<void> => {
    try {
      setState({ loading: true })
      const res = await numberv2.getPackageInfo(lang, controller.signal)
      setState({ loading: false })
      if (!controller.signal.aborted) {
        if (res !== undefined && res.error === undefined) {
          setState({ package: res })
        } else {
          showError(translate('request.failed'))
        }
      }
    } catch (e) {
      if (!controller.signal.aborted) {
        setState({ loading: false })
        console.error(e)
      }
    }
  }

  const disableContinue = (): boolean => {
    if (state.choosenCategory == null || state.choosenSubCat == null) {
      return true
    }
    if (state.choosenSubCat.selectedType === 'SELECT_SERVICE') {
      if (
        state.choosenDataMM?.id === 'c7677ed2-0a6f-4ea0-a050-cff4f25d084f' &&
        state.choosenCallMM == null
      ) {
        return true
      }
      if (
        state.choosenCallMM?.price === 10000 &&
        state.choosenDataMM === undefined
      ) {
        return true
      }
      if (
        state.choosenDataMM?.price === 15000 &&
        state.choosenCallMM === undefined
      ) {
        return true
      }
      if (
        state.choosenDataMM == null &&
        state.choosenCallMM?.id === '33d15647-aa10-4a7c-92b7-08f6b504a714'
      ) {
        return true
      }
      if (
        state.choosenDataMM === undefined &&
        state.choosenCallMM === undefined
      ) {
        return true
      }
    } else {
      if (state.choosenPackage == null) {
        return true
      }
    }
    return false
  }

  const handleChoosePackage = (
    packageI: PackageDetailInfo,
    services: serviceDetail
  ): void => {
    const updatedCall =
      packageI.name === 'Call' || packageI.name === 'Яриа'
        ? state.choosenCallMM === services
          ? undefined
          : services
        : state.choosenCallMM

    const updatedData =
      packageI.name === 'Data' || packageI.name === 'Дата'
        ? state.choosenDataMM === services
          ? undefined
          : services
        : state.choosenDataMM

    const updatedSms =
      packageI.name === 'Мессэж' || packageI.name === 'Message'
        ? state.choosenSmsMM === services
          ? undefined
          : services
        : state.choosenSmsMM

    const updatedVideo =
      packageI.name === 'Video streaming' ||
      packageI.name === 'Видео стриминг үйлчилгээ'
        ? state.choosenVideoStreamingMM === services
          ? undefined
          : services
        : state.choosenVideoStreamingMM

    let updatedEntertainment = state.choosenEntertainmentMM ?? []
    if (
      packageI.name === 'Энтертайнмент дата багцууд' ||
      packageI.name === 'Entertainment data packages'
    ) {
      const alreadySelected = updatedEntertainment.some(
        (s) => s.id === services.id
      )
      updatedEntertainment = alreadySelected
        ? updatedEntertainment.filter((s) => s.id !== services.id)
        : [...updatedEntertainment, services]
    }

    setState({
      choosenPackage: state.choosenPackage,
      choosenCallMM: updatedCall,
      choosenDataMM: updatedData,
      choosenSmsMM: updatedSms,
      choosenVideoStreamingMM: updatedVideo,
      choosenEntertainmentMM: updatedEntertainment
    })

    if (onSelectionChange) {
      onSelectionChange({
        choosenPackage:
          state.choosenSubCat?.selectedType === 'SELECT_SERVICE'
            ? undefined
            : state.choosenPackage,
        choosenCallMM: updatedCall,
        choosenDataMM: updatedData,
        choosenSmsMM: updatedSms,
        choosenVideoStreamingMM: updatedVideo,
        choosenEntertainmentMM: updatedEntertainment
      })
    }
  }

  const handleSavePackage = async (): Promise<void> => {
    let req: SavePackageInfoReq
    const packages = [
      {
        name: state.choosenCallMM?.name ?? '',
        id: state.choosenCallMM?.id ?? ''
      },
      {
        name: state.choosenDataMM?.name ?? '',
        id: state.choosenDataMM?.id ?? ''
      },
      {
        name: state.choosenSmsMM?.name ?? '',
        id: state.choosenSmsMM?.id ?? '',
        isAdditionalService: true
      },
      {
        name: state.choosenVideoStreamingMM?.name ?? '',
        id: state.choosenVideoStreamingMM?.id ?? '',
        isAdditionalService: true
      },
      ...(state.choosenEntertainmentMM?.map((ent) => ({
        name: ent.name ?? '',
        id: ent.id ?? '',
        isAdditionalService: true
      })) ?? [])
    ]

    const filteredPackages = packages.filter(
      (pkg) => pkg.name !== '' || pkg.id !== ''
    )

    try {
      if (state.choosenSubCat?.selectedType === 'SELECT_SERVICE') {
        req = {
          instanceId,
          rateplan: state.choosenSubCat.rateplan ?? '',
          category: {
            categoryId: state.choosenCategory?.categoryId ?? '',
            categoryName: state.choosenCategory?.categoryName ?? ''
          },
          subCategory: {
            id: state.choosenSubCat.id,
            name: state.choosenSubCat.name
          },
          packages: filteredPackages,
          referral: {
            referralCode: referralCode !== undefined ? referralCode : '',
            promoCode: 'REFERRAL-PROMO'
          }
        }
      } else {
        req = {
          instanceId,
          rateplan: state.choosenPackage?.rateplan ?? '',
          category: {
            categoryId: state.choosenCategory?.categoryId ?? '',
            categoryName: state.choosenCategory?.categoryName ?? ''
          },
          subCategory: {
            id: state.choosenSubCat?.id ?? '',
            name: state.choosenSubCat?.name ?? ''
          },
          packages: [
            {
              name: state.choosenPackage?.name ?? '',
              id: state.choosenPackage?.id ?? ''
            }
          ],
          referral: {
            referralCode: referralCode !== undefined ? referralCode : '',
            promoCode: 'REFERRAL-PROMO'
          }
        }
      }
      const res = await numberv2.savePackageInfo(
        req,
        isWebView ? 'mobicom_app' : 'mobicom_web'
      )
      if (res !== undefined && res.error === undefined) {
        next({
          rateplan: res.rateplan,
          choosenPackage:
            (state.choosenSubCat?.name ?? '') +
            ' - ' +
            (state.choosenSubCat?.selectedType === 'SELECT_SERVICE'
              ? (state.choosenCallMM?.name ?? '') +
                '/' +
                (state.choosenDataMM?.name ?? '')
              : state.choosenPackage?.name ?? ''),
          choosenInfo: {
            choosenPackage: state.choosenPackage,
            choosenCallMM: state.choosenCallMM,
            choosenDataMM: state.choosenDataMM,
            choosenSmsMM: state.choosenSmsMM,
            choosenVideoStreamingMM: state.choosenVideoStreamingMM,
            choosenEntertainmentMM: state.choosenEntertainmentMM
          }
        })
      } else {
        showError(
          locale === 'en'
            ? res?.cause?.messageEn ?? res?.error ?? translate('request.failed')
            : res?.cause?.messageMn ?? res?.error ?? translate('request.failed')
        )
      }
    } catch (e) {
      showError(translate('request.failed'))
    } finally {
      setState({ loading: false })
    }
  }

  if (state.package === undefined && state.loading === true) {
    return (
      <div className='flex items-center justify-center w-full'>
        <Loader />
      </div>
    )
  }

  return (
    <div className='flex flex-col items-start justify-center gap-4 p-6 bg-surfaceMid rounded-xl md:p-8'>
      <div className='flex justify-center w-full gap-1'>
        {state.package?.categories.map((item, i) => (
          <div key={i} className='flex flex-col w-full gap-4'>
            <div
              className={
                'rounded-full cursor-pointer flex justify-center items-center font-bold px-3 py-2 border ' +
                (item === state.choosenCategory
                  ? 'bg-secondary text-textInverse'
                  : 'border-secondaryMidStroke bg-surfaceInverse text-textHigh')
              }
              onClick={() =>
                setState({ choosenCategory: item, choosenSubCat: undefined })
              }
            >
              <span className='text-sm text-center'>{item.categoryName}</span>
            </div>
          </div>
        ))}
      </div>
      {state.choosenCategory?.subCategories !== undefined &&
      state.choosenCategory?.subCategories.length > 0 ? (
        <div className='flex flex-col w-full gap-4'>
          <span className='text-sm font-bold text-textMid'>
            {translate('package')}
          </span>
          <TextField
            label={translate('package.choose')}
            variant='outlined'
            className='w-full rounded-lg border-secondaryHighStroke bg-surfaceInverse text-textMid'
            value={state.choosenSubCat?.name ?? ''}
            select
            SelectProps={{
              MenuProps: {
                sx: { maxHeight: '40%' },
                PaperProps: {
                  sx: {
                    bgcolor: 'rgba(var(--surfaceInverse))',
                    color: 'var(--color-text-textHigh)',
                    borderRadius: '8px'
                  }
                }
              }
            }}
            InputLabelProps={{
              style: { color: 'var(--color-text-textHigh)' }
            }}
            InputProps={{
              style: {
                color: 'var(--color-text-textHigh)',
                '&::placeholder': {
                  color: 'var(--color-text-secondaryHighStroke)'
                }
              } as any
            }}
            sx={{
              '& fieldset': {
                borderRadius: '8px'
              },
              '& .MuiSelect-icon': {
                color:
                  theme === 'dark'
                    ? 'var(--color-text-textHigh)' // or your preferred dark theme color
                    : 'var(--color-text-secondaryHighStroke)'
              }
            }}
          >
            {state.choosenCategory?.subCategories.map((item, i) => (
              <MenuItem
                key={i}
                value={item.name}
                onClick={() => handleSubCategoryClick(item)}
                className='text-sm bg-surfaceInverse text-textHigh'
              >
                {item.name}
              </MenuItem>
            ))}
          </TextField>
        </div>
      ) : (
        <></>
      )}
      {state.choosenSubCat?.packages !== undefined &&
      state.choosenSubCat?.packages.length > 0 ? (
        <div className='flex flex-col w-full gap-3'>
          {state.choosenSubCat?.id ===
          '6bd9c88c-26f6-4d3b-9131-9b22a7578b56' ? (
            <div className='grid w-full grid-cols-2 gap-1 overflow-auto'>
              {state.choosenSubCat.packages.map((item, i) => (
                <div
                  key={i}
                  onClick={
                    state.choosenPackage === item
                      ? () => {
                          setState({ choosenPackage: undefined })
                          onSelectionChange?.({})
                        }
                      : () => {
                          setState({ choosenPackage: item })
                          onSelectionChange?.({
                            choosenPackage: item,
                            choosenCallMM: undefined,
                            choosenDataMM: undefined,
                            choosenSmsMM: undefined,
                            choosenVideoStreamingMM: undefined,
                            choosenEntertainmentMM: []
                          })
                        }
                  }
                  className={classNames(
                    'border rounded-xl p-3 cursor-pointer flex justify-between items-start bg-surfaceInverse',
                    state.choosenPackage === item
                      ? 'border-secondaryMainStroke'
                      : 'border-secondaryMidStroke'
                  )}
                >
                  <div className='flex flex-col w-5/6'>
                    <span className='text-lg font-bold text-textHigh'>
                      {item.name}
                    </span>
                    {item.services.map((services, idx) => (
                      <span key={idx} className='text-xs truncate text-textMid'>
                        {services.name}
                      </span>
                    ))}
                    <span className='mt-3 text-sm text-textHigh'>
                      {item.price}₮
                    </span>
                  </div>
                  <Checkbox
                    color='error'
                    icon={
                      <RadioButtonUncheckedOutlined className='text-textHigh' />
                    }
                    checkedIcon={<CheckCircle className='text-primaryMain' />}
                    className='p-0'
                    checked={state.choosenPackage === item}
                  />
                </div>
              ))}
            </div>
          ) : (
            state.choosenSubCat.packages.map((item, i) =>
              state.choosenSubCat?.selectedType === 'SELECT_SERVICE' ? (
                item.isAdditionalService !== true && (
                  <div key={i} className='flex flex-col gap-2'>
                    <span className='text-sm font-bold text-textMid'>
                      {item.name}
                    </span>
                    <div className='grid grid-cols-2 gap-1'>
                      {item.services.map((services, idx) => (
                        <div
                          key={idx}
                          className={classNames(
                            'relative border rounded-xl px-2 py-3 flex justify-between items-start cursor-pointer bg-surfaceInverse overflow-hidden',
                            state.choosenCallMM === services ||
                              state.choosenDataMM === services ||
                              state.choosenSmsMM === services ||
                              state.choosenVideoStreamingMM === services ||
                              (state.choosenEntertainmentMM ?? []).some(
                                (s) => s.id === services.id
                              )
                              ? 'border-secondaryMainStroke'
                              : 'border-secondaryMidStroke'
                          )}
                          onClick={() => handleChoosePackage(item, services)}
                        >
                          <div className='flex flex-col w-5/6 gap-2 justify-between h-full'>
                            <div className='flex flex-col items-start gap-1'>
                              <span
                                className={classNames(
                                  'text-textHigh',
                                  item.name === 'Data' || item.name === 'Дата'
                                    ? 'font-bold text-lg'
                                    : 'text-sm'
                                )}
                              >
                                {services.name}
                              </span>
                              <div className='flex flex-col items-start'>
                                {typeof services.vooPackage === 'string' &&
                                  services.vooPackage.trim() !== '' && (
                                    <span className='text-xs text-textMid font-normal'>
                                      {services.vooPackage}
                                    </span>
                                  )}
                                {services.hasMobiAI === true && (
                                  <span className='text-xs text-textMid font-normal'>
                                    MobiAI
                                  </span>
                                )}
                              </div>
                            </div>
                            <span className='text-xs text-textMid'>
                              {moneyFormatter(services.price)}
                            </span>
                          </div>
                          <Checkbox
                            color='error'
                            icon={
                              <RadioButtonUncheckedOutlined className='text-textHigh' />
                            }
                            checkedIcon={
                              <CheckCircle className='text-primaryMain' />
                            }
                            className='p-0'
                            checked={
                              state.choosenCallMM === services ||
                              state.choosenDataMM === services ||
                              state.choosenSmsMM === services ||
                              state.choosenVideoStreamingMM === services ||
                              (state.choosenEntertainmentMM ?? []).some(
                                (s) => s.id === services.id
                              )
                            }
                          />
                          {services.is5GSupported === true && (
                            <div className='absolute bottom-0 right-0 bg-primaryMain rounded-tl-xl px-2'>
                              <span className='text-textAdditional text-xs font-semibold truncate'>
                                5G
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ) : (
                <div
                  key={i}
                  onClick={
                    state.choosenPackage === item
                      ? () => {
                          setState({ choosenPackage: undefined })
                          onSelectionChange?.({})
                        }
                      : () => {
                          setState({ choosenPackage: item })
                          onSelectionChange?.({
                            choosenPackage: item,
                            choosenCallMM: undefined,
                            choosenDataMM: undefined,
                            choosenSmsMM: undefined,
                            choosenVideoStreamingMM: undefined,
                            choosenEntertainmentMM: []
                          })
                        }
                  }
                  className={classNames(
                    'border rounded-xl p-3 cursor-pointer flex justify-between items-start  bg-surfaceInverse',
                    state.choosenPackage === item
                      ? 'border-secondaryMainStroke'
                      : 'border-secondaryMidStroke'
                  )}
                >
                  <div className='flex flex-col w-5/6'>
                    <span className='text-lg font-bold text-textHigh'>
                      {item.name}
                    </span>
                    <p className='text-xs truncate text-textMid'>
                      {item.services.map((services, idx) => (
                        <span key={idx}>
                          {services.name}
                          {idx < item.services.length - 1 && <br />}
                        </span>
                      ))}
                    </p>
                    <span className='mt-3 text-sm text-textHigh'>
                      {item.price}₮
                    </span>
                  </div>
                  <Checkbox
                    color='error'
                    icon={
                      <RadioButtonUncheckedOutlined className='text-textHigh' />
                    }
                    checkedIcon={<CheckCircle className='text-primaryMain' />}
                    className='p-0'
                    checked={
                      state.choosenPackage === item ||
                      (state.choosenSubCat?.id ===
                        '24aa6cc9-71cf-49ac-9c5b-136c93b1f147' &&
                        i === 0)
                    }
                  />
                </div>
              )
            )
          )}
          {state.choosenSubCat?.selectedType === 'SELECT_SERVICE' &&
            addonPackages !== undefined &&
            addonPackages.length > 0 && (
              <div className='mt-6 w-full border border-secondarySoftLow p-4 rounded-xl bg-surfaceInverse'>
                <div className='flex flex-col items-start gap-1 pb-4'>
                  <span className='text-base font-bold text-textHigh'>
                    {translate('package.addOnPackage')}
                  </span>
                  <span className='text-xs text-textMid font-medium'>
                    {translate('package.addOnPackageDescription')}
                  </span>
                </div>
                <div className='relative flex items-start justify-start w-full'>
                  <div
                    ref={scrollRef}
                    className='flex gap-2 overflow-x-auto no-scrollbar'
                  >
                    {addonPackages.map((addon, index) => (
                      <div
                        key={index}
                        className={classNames(
                          'flex-shrink-0 px-3 py-1 rounded-full cursor-pointer',
                          selectedAddonTab === index
                            ? 'bg-secondary text-textInverse'
                            : 'bg-surfaceInverse text-textHigh border border-secondaryMidStroke'
                        )}
                        onClick={() => setSelectedAddonTab(index)}
                      >
                        <span className='text-sm font-semibold'>
                          {addon.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className='flex flex-col gap-2 mt-3'>
                  <div className='grid grid-cols-2 gap-1'>
                    {addonPackages[selectedAddonTab]?.services.map(
                      (services, idx) => (
                        <div
                          key={idx}
                          className={classNames(
                            'border rounded-xl flex flex-col justify-between items-start cursor-pointer bg-surfaceInverse',
                            state.choosenCallMM === services ||
                              state.choosenDataMM === services ||
                              state.choosenSmsMM === services ||
                              state.choosenVideoStreamingMM === services ||
                              (state.choosenEntertainmentMM ?? []).some(
                                (s) => s.id === services.id
                              )
                              ? 'border-secondaryMainStroke'
                              : 'border-secondaryMidStroke'
                          )}
                          onClick={() =>
                            handleChoosePackage(
                              addonPackages[selectedAddonTab],
                              services
                            )
                          }
                        >
                          <div className='gap-2 flex flex-col p-2 justify-between w-full h-full items-start'>
                            <div className='justify-between items-start flex w-full'>
                              <div className='flex w-6 h-6 rounded-2xl border border-secondarySoftLow items-center justify-center overflow-hidden'>
                                <img
                                  src={services.img}
                                  alt='icon'
                                  className='w-full h-full object-cover'
                                />
                              </div>
                              <div
                                className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors duration-300 ${
                                  state.choosenCallMM === services ||
                                  state.choosenDataMM === services ||
                                  state.choosenSmsMM === services ||
                                  state.choosenVideoStreamingMM === services ||
                                  (state.choosenEntertainmentMM ?? []).some(
                                    (s) => s.id === services.id
                                  )
                                    ? 'bg-primaryMain'
                                    : 'bg-secondarySoftLow'
                                }`}
                              >
                                <div
                                  className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${
                                    state.choosenCallMM === services ||
                                    state.choosenDataMM === services ||
                                    state.choosenSmsMM === services ||
                                    state.choosenVideoStreamingMM ===
                                      services ||
                                    (state.choosenEntertainmentMM ?? []).some(
                                      (s) => s.id === services.id
                                    )
                                      ? 'translate-x-4'
                                      : 'translate-x-0'
                                  }`}
                                />
                              </div>
                            </div>
                            <div className='flex justify-center items-center'>
                              <span className='text-textHigh text-xs font-bold'>
                                {services.name}
                              </span>
                            </div>
                          </div>
                          <div className='flex h-10 p-2 bg-surfaceLow rounded-b-xl justify-between items-center w-full'>
                            {services.dataVolume && (
                              <span className='text-textHigh text-base font-bold '>
                                {services.dataVolume}
                              </span>
                            )}
                            <span className='text-textMid text-sm font-medium'>
                              {(services.price ?? 0)
                                .toString()
                                .replace(/\B(?=(\d{3})+(?!\d))/g, '’')}
                              ₮
                            </span>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
        </div>
      ) : (
        <></>
      )}
      {state.choosenCategory !== undefined &&
      state.choosenSubCat !== undefined ? (
        <div className='flex justify-end w-full'>
          <DarkButton
            onClick={() => {
              void handleSavePackage()
            }}
            disabled={disableContinue()}
          >
            {translate('choose')}
          </DarkButton>
        </div>
      ) : null}
    </div>
  )
}
export default ChoosePackage
